import React, { FC, ReactElement } from 'react';

import classNames from 'classnames';
import Slider, { SliderProps } from 'rc-slider';

import 'rc-slider/assets/index.css';
import './input-slider.scss';

const SINGLE_LEVEL_AMOUNT = 1;
const SINGLE_LEVEL_SLIDER_MIN = 0;
const SINGLE_LEVEL_SLIDER_MAX = 1;

interface IInputSliderProps extends SliderProps {
  /**
   * A save is in flight: the control ignores input and dims calmly, rather than
   * taking on the library's grey disabled look.
   */
  busy?: boolean;
}

// Position only. Colour and the line's thickness live in input-slider.scss (roles and
// CSS variables), so the slider follows the theme and its states stay in one place.
const dotStyles: React.CSSProperties = {
  width: '4px',
  height: '4px',
  top: '50%',
  bottom: 'auto',
  transform: 'translate(-50%, -50%)',
  borderRadius: '50%',
  border: 'none',
};

const railStyles: React.CSSProperties = {
  // rc-slider positions endpoint dots and handles at 0% and 100%. Extend the
  // visual rail by the 8px handle radius so those endpoint centers sit inside
  // its rounded caps, matching the Figma component.
  left: '-8px',
  width: 'calc(100% + 16px)',
  top: '50%',
  transform: 'translateY(-50%)',
};

const InputSlider: FC<IInputSliderProps> = props => {
  const { busy, ...sliderOwnProps } = props;
  const { disabled, max: maxLengthLevel } = sliderOwnProps;

  const isMaxLengthLevel = maxLengthLevel === SINGLE_LEVEL_AMOUNT;
  const sliderProps: SliderProps = isMaxLengthLevel
    ? {
        ...sliderOwnProps,
        min: SINGLE_LEVEL_SLIDER_MIN,
        max: SINGLE_LEVEL_SLIDER_MAX,
        value: SINGLE_LEVEL_SLIDER_MAX,
        step: null,
        marks: {
          [SINGLE_LEVEL_SLIDER_MAX]: { label: ' ', style: { display: 'none' } },
        },
      }
    : sliderOwnProps;

  const trackStyles: React.CSSProperties = {
    top: '50%',
    transform: 'translateY(-50%)',
    ...(isMaxLengthLevel ? { width: '100%' } : {}),
  };

  const handleCustomized = (handleElement: ReactElement): ReactElement => {
    const transform = handleElement.props.style.transform
      ? `${handleElement.props.style.transform} translateY(-50%)`
      : 'translateY(-50%)';

    return (
      <div
        {...handleElement.props}
        className={`${handleElement.props.className} handle-customized`}
        style={{
          ...handleElement.props.style,
          transform,
        }}
      >
        <div className="handle-customized__inner" />
      </div>
    );
  };

  return (
    <div
      className={classNames('rs-slider-wrapper', {
        // One level can't be moved, so it is shown as a plain full bar, without a
        // handle that would imply otherwise.
        'rs-slider-wrapper--static': isMaxLengthLevel,
        'rs-slider-wrapper--busy': busy,
      })}
      aria-busy={busy || undefined}
    >
      <Slider
        {...sliderProps}
        handleRender={handleCustomized}
        dots={!isMaxLengthLevel}
        dotStyle={dotStyles}
        railStyle={railStyles}
        trackStyle={trackStyles}
        disabled={isMaxLengthLevel || disabled || busy}
      />
    </div>
  );
};

export { InputSlider };
